import {
  Component,
  input,
  output,
  effect,
  signal,
  ViewChild,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormMode } from 'app/core/constants/form-mode';
import { CatalogService } from '@services/data/catalog.service';
import { ClaimService } from '../../services/claim.service';
import { RequestService } from '../../../management-request/services/requests.service';
import { ProcessDocumentService } from '../../../management/process-document-management/services/process-document.service';
import { DocumentGroupComponent } from '../../../management-document/document-group/document-group.component';
import { DocumentApprovalComponent } from '../../../management-document/components/document-approval/document-approval.component';
import { SheetSelectComponent } from 'app/core/components/sheet-select/sheet-select.component';
import { AppSettings } from '@services/settings/app-settings.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-claim-detail-document',
  templateUrl: './claim-detail-document.component.html',
  styleUrls: ['./claim-detail-document.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    DocumentGroupComponent,
    DocumentApprovalComponent,
    SheetSelectComponent
  ],
  providers: [ClaimService]
})
export class ClaimDetailDocumentComponent {
  detail = input<any>(null);
  claimData = input<any>(null);
  mode = input<FormMode>(FormMode.VIEW);
  canEditDetails = input<boolean>(false);
  canReplaceObserved = input<boolean>(false);
  canFinalApproveDocs = input<boolean>(false);

  documentUploaded = output<void>();

  formMode = FormMode;
  appSettings = inject(AppSettings);
  private catalogService = inject(CatalogService);
  private claimService = inject(ClaimService);
  private requestService = inject(RequestService);
  private processService = inject(ProcessDocumentService);

  @ViewChild(DocumentApprovalComponent) documentApproval?: DocumentApprovalComponent;

  detailDocuments = signal<any>({ categories: [] });
  selectedDetailDocs = signal<any[]>([]);
  availableOptionalDocs = signal<any[]>([]);
  selectedOptionalTypeSlug: string | null = null;

  private Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true
  });

  constructor() {
    effect(() => {
      const claim = this.claimData();
      const detail = this.detail();
      if (claim && detail && (detail.id || detail.uuid)) {
        this._syncDocs(claim, detail);
      } else {
        this.detailDocuments.set({ categories: [] });
        this.selectedDetailDocs.set([]);
        this.availableOptionalDocs.set([]);
      }
    });
  }

  showDocumentUpload(): boolean {
    return this.mode() !== FormMode.VIEW;
  }

  get canComplementDocuments(): boolean {
    const det = this.detail();
    if (!det?.id && !det?.uuid) return false;
    if (det?.statusSlug === 'REVIEWING') return false;
    if (this.mode() === FormMode.VIEW && !this.canEditDetails()) return false;
    if (!this.showDocumentUpload()) return false;
    return this.availableOptionalDocs().length > 0;
  }

  /** Motivo para bloquear Dejar en revisión (evidencias faltantes). */
  closeBlockedReason(): string {
    const det = this.detail();
    if (!det?.id && !det?.uuid) return 'Guarde el daño primero.';
    const required = (this.detailDocuments()?.categories ?? [])
      .flatMap((c: any) => c.documents ?? [])
      .filter((d: any) => d.isRequired !== false);
    if (!required.length) return 'Aún no hay evidencias requeridas.';
    const missing = required.filter((d: any) => !d.filePath).length;
    if (missing) return `Faltan ${missing} evidencia(s) por cargar.`;
    return '';
  }

  buildDocsList(detail: any = this.detail()): any[] {
    if (!detail) return [];
    const docs = detail.fileName
      ? [{ name: 'Presupuesto (Local)', fileName: detail.fileName, isLocal: true, file: detail.file }]
      : [];
    const serverDocs = (this.claimData()?.documents ?? [])
      .filter((d: any) => this._matchesDetail(d, detail.id ?? detail.uuid, detail.uuid))
      .map((d: any) => this._mapServerDoc(d));
    return [...docs, ...serverDocs];
  }

  observedDocsForDetail(det: any = this.detail()): any[] {
    const fromClaim = this.buildDocsList(det);
    const fromSelected = this.selectedDetailDocs();
    const fromCategories = (this.detailDocuments()?.categories ?? [])
      .flatMap((c: any) => c.documents ?? []);
    const fromApproval = this.documentApproval?.documents() ?? [];
    const byKey = new Map<string, any>();
    for (const d of [...fromClaim, ...fromSelected, ...fromCategories, ...fromApproval]) {
      if (!d || d.isLocal) continue;
      const key = String(d.uuid || d.id || d.documentUuid || '');
      if (!key) continue;
      byKey.set(key, d);
    }
    return [...byKey.values()].filter((d) => this._docIsObserved(d));
  }

  approveAllDetailDocuments(det: any = this.detail()): void {
    const docs = this.buildDocsList(det).filter((d) => !d.isLocal && d.uuid);
    docs.forEach((d) => {
      if (d.statusSlug !== 'APPROVED') {
        this.requestService.updateDocument({ id: d.uuid, statusSlug: 'APPROVED', origin: 'CLAIM' }).subscribe();
      }
    });
  }

  onEvidenceReviewCompleted(): void {
    const live = this.documentApproval?.documents() ?? [];
    const det = this.detail();
    const claim = this.claimData();
    if (live.length && claim && det) {
      const docs = (claim.documents ?? []).map((d: any) => {
        const match = live.find(
          (l: any) => l.uuid === d.uuid || l.id === d.uuid || l.documentUuid === d.uuid
        );
        if (!match) return d;
        return {
          ...d,
          statusSlug: match.statusSlug ?? d.statusSlug,
          reviewComment: match.reviewComment ?? d.reviewComment
        };
      });
      this.selectedDetailDocs.set(this.buildDocsList({ ...det }));
      this.detailDocuments.set(this.getFormattedDocs(det, { ...claim, documents: docs }));
    }
    this.documentUploaded.emit();
  }

  uploadDetailDocument(event: {
    uuid: string;
    file: File;
    expirationDate?: string | null;
    done?: (ok: boolean) => void;
  }): void {
    this.claimService.uploadClaimDocument(event.uuid, event.file, {
      expirationDate: event.expirationDate || null
    }).subscribe({
      next: () => {
        this.Toast.fire({ icon: 'success', title: 'Subido con éxito' });
        event.done?.(true);
        this.documentUploaded.emit();
      },
      error: (e) => {
        this.Toast.fire({ icon: 'error', title: e.error?.message ?? 'No se pudo subir.' });
        event.done?.(false);
      }
    });
  }

  addOptionalDocument(): void {
    const slug = this.selectedOptionalTypeSlug;
    if (!slug) return;
    const req = this.availableOptionalDocs().find((r) => r.typeSlug === slug);
    if (!req) return;
    const claim = this.claimData();
    const detail = this.detail();
    const payload = {
      claimId: claim?.id,
      requestId: claim?.requestId,
      claimDetailId: detail?.id,
      processSlug: claim?.claimType,
      ownerSlug: req.ownerSlug ?? 'PARTY',
      typeSlug: req.typeSlug,
      categorySlug: req.categorySlug,
      statusSlug: 'PENDING',
      version: 1
    };
    Swal.fire({ title: 'Agregando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.claimService.createDocumentClaim(payload).subscribe({
      next: () => {
        Swal.close();
        this.selectedOptionalTypeSlug = null;
        this.availableOptionalDocs.update((opts) => opts.filter((o) => o.typeSlug !== slug));
        this.Toast.fire({ icon: 'success', title: 'Documento agregado' });
        this.documentUploaded.emit();
        this._refreshDetailDocs();
      },
      error: (err) => {
        Swal.close();
        const msg = err?.error?.error ?? err?.error?.message ?? 'No se pudo agregar el documento';
        Swal.fire('Error', msg, 'error');
      }
    });
  }

  removeOptionalDocument(uuid: string): void {
    if (!uuid) return;
    const docs = (this.detailDocuments()?.categories ?? []).flatMap((c: any) => c.documents ?? []);
    const doc = docs.find((d: any) => d.uuid === uuid || d.id === uuid);
    if (doc && doc.isRequired !== false) {
      this.Toast.fire({ icon: 'warning', title: 'Los obligatorios no se pueden eliminar' });
      return;
    }
    Swal.fire({ title: 'Eliminando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    this.claimService.deleteDocumentClaim(uuid).subscribe({
      next: () => {
        Swal.close();
        this._removeDocFromLocalState(uuid, doc);
        this.Toast.fire({ icon: 'success', title: 'Documento opcional eliminado' });
        this.documentUploaded.emit();
        this._refreshDetailDocs();
      },
      error: () => Swal.fire('Error', 'No se pudo eliminar el documento', 'error')
    });
  }

  private _syncDocs(claim: any, detail: any): void {
    this.selectedDetailDocs.set(this.buildDocsList(detail));
    this.detailDocuments.set(this.getFormattedDocs(detail, claim));
    this.loadAvailableOptionalDocs(detail);
  }

  private _refreshDetailDocs(): void {
    setTimeout(() => {
      const detail = this.detail();
      const claim = this.claimData();
      if (!detail || !claim) return;
      this._syncDocs(claim, detail);
    }, 300);
  }

  getFormattedDocs(detail: any, claim: any = this.claimData()): any {
    if (!claim) return { categories: [] };
    const categoriesMap = new Map<string, any>();
    const detailId = detail?.id ?? detail?.uuid;
    if (!detailId) return { categories: [] };
    const filteredDocs = (claim.documents || []).filter((d: any) =>
      this._matchesDetail(d, detailId, detail.uuid)
    );
    filteredDocs.forEach((d: any) => this._addDocToLocalCategory(categoriesMap, d));
    return { partyId: claim.partyId, categories: Array.from(categoriesMap.values()) };
  }

  loadAvailableOptionalDocs(detail: any = this.detail()): void {
    if (this.mode() === FormMode.VIEW && !this.canEditDetails()) return;
    if (!detail?.id && !detail?.uuid) return;
    const exist = this._existingTypeSlugsForDetail();
    const snapItems = this._snapshotRequirementItems();
    if (snapItems) {
      this.availableOptionalDocs.set(this._mapOptionalFromItems(snapItems, exist));
      return;
    }
    const slug = this.claimData()?.claimType;
    if (!slug) return;
    this.processService.getRequirementsByProcess(slug).subscribe({
      next: (reqs) => this.availableOptionalDocs.set(this._mapOptionalFromItems(reqs, exist))
    });
  }

  private _existingTypeSlugsForDetail(): string[] {
    const fromFormatted: string[] = ((this.detailDocuments()?.categories ?? []) as any[])
      .flatMap((c: any) => (c?.documents ?? []) as any[])
      .map((d: any) => (d?.typeSlug != null ? String(d.typeSlug) : ''))
      .filter((slug) => slug.length > 0);
    if (fromFormatted.length) return Array.from(new Set<string>(fromFormatted));
    const fromSelected: string[] = this.selectedDetailDocs()
      .map((d: any) => (d?.typeSlug != null ? String(d.typeSlug) : ''))
      .filter((slug) => slug.length > 0);
    return Array.from(new Set<string>(fromSelected));
  }

  private _snapshotRequirementItems(): any[] | null {
    const snap = this.claimData()?.request?.documentRequirementsSnapshot
      ?? this.claimData()?.request?.document_requirements_snapshot;
    const items = Array.isArray(snap?.items) ? snap.items : null;
    return items?.length ? items : null;
  }

  private _mapOptionalFromItems(items: any[], exist: string[]): any[] {
    const taken = new Set(exist);
    return (items || [])
      .filter((r) => r.isRequired === false && r.typeSlug && !taken.has(r.typeSlug))
      .map((r) => ({
        typeSlug: r.typeSlug,
        name: r.type?.name ?? this.catalogService.getDocumentTypeName(r.typeSlug, r.typeSlug),
        categorySlug: r.categorySlug,
        ownerSlug: r.ownerSlug
      }));
  }

  private _removeDocFromLocalState(uuid: string, removedDoc?: any): void {
    const typeSlug = removedDoc?.typeSlug;
    this.detailDocuments.update((cur) => {
      if (!cur?.categories?.length) return cur;
      const categories = cur.categories
        .map((cat: any) => ({
          ...cat,
          documents: (cat.documents ?? []).filter((d: any) => d.uuid !== uuid && d.id !== uuid)
        }))
        .filter((cat: any) => (cat.documents ?? []).length > 0);
      return { ...cur, categories };
    });
    this.selectedDetailDocs.update((docs) =>
      docs.filter((d) => d.uuid !== uuid && d.id !== uuid)
    );
    const detail = this.detail();
    if (detail) this.loadAvailableOptionalDocs(detail);
    else if (typeSlug && removedDoc?.isRequired === false) {
      const name = removedDoc?.docTypeName
        ?? this.catalogService.getDocumentTypeName(typeSlug, typeSlug);
      this.availableOptionalDocs.update((opts) => {
        if (opts.some((o) => o.typeSlug === typeSlug)) return opts;
        return [...opts, {
          typeSlug,
          name,
          categorySlug: removedDoc?.categorySlug,
          ownerSlug: removedDoc?.ownerSlug
        }];
      });
    }
  }

  private _matchesDetail(d: any, detailId: any, detailUuid?: string): boolean {
    const docDetailId = d.claimDetailId ?? d.claim_detail_id;
    const matchId = docDetailId && detailId && docDetailId == detailId;
    const matchUuid = d.claimDetail?.uuid && detailUuid && d.claimDetail.uuid === detailUuid;
    return !!(matchId || matchUuid);
  }

  private _mapServerDoc(d: any) {
    const doc = d.document ?? {};
    return {
      uuid: d.uuid,
      statusSlug: d.statusSlug,
      claimDetailId: d.claimDetailId ?? d.claim_detail_id,
      claimDetailUuid: d.claimDetail?.uuid,
      name: d.type?.name ?? d.typeSlug,
      fileName: doc.fileName ?? 'Archivo',
      filePath: doc.uuid ? `V1/documents/view/${doc.uuid}` : null,
      mimeType: doc.mimeType ?? null,
      isLocal: false,
      typeSlug: d.typeSlug,
      origin: 'CLAIM',
      reviewComment: d.reviewComment
    };
  }

  private _addDocToLocalCategory(map: Map<string, any>, d: any): void {
    const cat = d.categorySlug ?? 'default';
    if (!map.has(cat)) {
      map.set(cat, {
        docCategoryCode: cat,
        categorySlug: cat,
        docCategoryName: d.category?.name ?? this._getCategoryNameBySlug(cat),
        documents: []
      });
    }
    map.get(cat).documents.push(this._mapDocItemForDetail(d));
  }

  private _getCategoryNameBySlug(slug: string): string {
    return slug === 'PHOTOS' ? 'Fotografías de Evidencia' : 'Documentación del Vehículo';
  }

  private _mapDocItemForDetail(d: any): any {
    const f = d.document ?? {};
    const snapRequired = this._isRequiredInSnapshot(d.typeSlug);
    return {
      id: d.uuid,
      uuid: d.uuid,
      documentUuid: d.uuid,
      fileUuid: f.uuid,
      fileName: f.fileName,
      filePath: f.uuid ? `V1/documents/view/${f.uuid}` : null,
      mimeType: f.mimeType,
      fileSize: f.fileSize,
      statusSlug: d.statusSlug,
      categorySlug: d.categorySlug,
      processSlug: d.processSlug,
      ownerSlug: d.ownerSlug,
      typeSlug: d.typeSlug,
      origin: 'CLAIM',
      reviewComment: d.reviewComment ?? null,
      statusName: d.status?.name ?? this.catalogService.getStatusBySlug(d.statusSlug)?.name,
      statusStyle: d.status?.style ?? this.catalogService.getStatusBySlug(d.statusSlug)?.style,
      docCategoryCode: d.categorySlug ?? 'default',
      docTypeCode: d.typeSlug,
      docTypeName: d.type?.name ?? d.typeSlug ?? 'Documento',
      isRequired: d.isRequired ?? snapRequired ?? d.type?.isRequired ?? true
    };
  }

  private _isRequiredInSnapshot(typeSlug: string | null | undefined): boolean | undefined {
    if (!typeSlug) return undefined;
    const items = this._snapshotRequirementItems();
    if (!items) return undefined;
    const hit = items.find((i) => i.typeSlug === typeSlug);
    if (!hit) return undefined;
    return hit.isRequired !== false;
  }

  private _docIsObserved(d: any): boolean {
    const slug = String(d?.statusSlug || d?.status?.slug || d?.status_slug || '').toUpperCase();
    if (slug === 'OBSERVED') return true;
    return slug === 'PENDING' && !!String(d?.reviewComment || d?.review_comment || '').trim();
  }
}
